import React, { useEffect, useState } from 'react';
import { Box, Card, CardMedia, Typography } from '@mui/material';
import { Helmet } from 'react-helmet';
import { useNavigate, useParams } from 'react-router-dom';

import Button from '../components/Button';
import CheckoutPageHeader from '../components/checkout/CheckoutPageHeader';
import { paths } from '../config/paths';
import { v2Colors, v2DisplayFontFamily } from '../design/theme';
import PageContainer from '../layouts/PageContainer';
import PageState from '../layouts/PageState';
import { fetchBlogPost, logBlogPostView } from '../services/blogs';
import { formatBlogDate, getMetaDescription } from '../utils/blog';

export default function BlogPost() {
  const { urlId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [blogPost, setBlogPost] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    setBlogPost(null);

    fetchBlogPost(urlId)
      .then((post) => {
        if (cancelled) return;
        setBlogPost(post);
        logBlogPostView(urlId);
      })
      .catch((err) => {
        console.error('Error fetching blog post:', err);
        if (!cancelled) setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [urlId]);

  const seoTitle = blogPost?.seo?.title || blogPost?.title || 'Blog | Country Chicken Co';
  const seoDescription = blogPost?.seo?.description || getMetaDescription(blogPost?.content || '');
  const seoKeywords = blogPost?.seo?.keywords || '';

  return (
    <PageContainer sx={{ px: { xs: 1.5, sm: 2 }, pb: 4, maxWidth: { xs: 720, md: 800 } }}>
      {blogPost ? (
        <Helmet>
          <title>{seoTitle}</title>
          <meta name="description" content={seoDescription} />
          {seoKeywords ? <meta name="keywords" content={seoKeywords} /> : null}
          <meta property="og:title" content={seoTitle} />
          <meta property="og:description" content={seoDescription} />
          <meta property="og:type" content="article" />
          <meta property="og:url" content={window.location.href} />
          {blogPost.coverImage ? <meta property="og:image" content={blogPost.coverImage} /> : null}
          <link rel="canonical" href={window.location.href} />
        </Helmet>
      ) : (
        <Helmet>
          <title>Blog | Country Chicken Co</title>
        </Helmet>
      )}

      <CheckoutPageHeader backTo={paths.blogs} title="Blog" />

      {error ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '40vh',
            gap: 2,
            px: 2,
            textAlign: 'center',
          }}
        >
          <Typography variant="h6">Blog post not found</Typography>
          <Button onClick={() => navigate(paths.blogs)}>Back to Blogs</Button>
        </Box>
      ) : (
      <PageState loading={loading}>
        {blogPost ? (
          <Card
            elevation={0}
            sx={{
              p: { xs: 2, md: 2.5 },
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            {blogPost.coverImage ? (
              <CardMedia
                component="img"
                image={blogPost.coverImage}
                alt={blogPost.title}
                sx={{
                  objectFit: 'cover',
                  borderRadius: 1.5,
                  mb: 2.5,
                  maxHeight: 400,
                  width: '100%',
                  bgcolor: v2Colors.surface,
                }}
              />
            ) : null}

            <Typography
              component="h1"
              sx={{
                fontFamily: v2DisplayFontFamily,
                fontWeight: 700,
                fontSize: { xs: '1.5rem', md: '1.85rem' },
                lineHeight: 1.2,
                color: v2Colors.heading,
                mb: 1,
              }}
            >
              {blogPost.title}
            </Typography>

            <Typography variant="body2" sx={{ color: v2Colors.textMuted, mb: 2 }}>
              {blogPost.author ? `By ${blogPost.author}` : ''}
              {blogPost.author && blogPost.createdAt ? ' · ' : ''}
              {formatBlogDate(blogPost.createdAt, { year: 'numeric', month: 'long', day: 'numeric' })}
            </Typography>

            <Box
              dangerouslySetInnerHTML={{ __html: blogPost.content || '' }}
              sx={{
                lineHeight: 1.7,
                color: 'text.primary',
                '& img': { maxWidth: '100%', height: 'auto', borderRadius: 1 },
                '& iframe': { maxWidth: '100%' },
                '& h1, & h2, & h3': { fontWeight: 700, mt: 2.5, mb: 1, color: v2Colors.heading },
                '& p': { mb: 1.5 },
                '& ul, & ol': { pl: 2.5, mb: 1.5 },
                '& a': { color: 'primary.main' },
              }}
            />
          </Card>
        ) : null}
      </PageState>
      )}
    </PageContainer>
  );
}
